import { Segmented } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { FC } from 'react'

import { Button } from 'common/components/Button/Button'
import { DatePicker } from 'common/components/DatePicker/DatePicker'
import { Label } from 'common/components/Label/Label'
import { Select } from 'common/components/Select/Select'
import { BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'

import { WINDOW_OPTIONS, type SearchCriteria } from './ehrAppointmentV2.types'
import styles from './ehrAppointmentV2.module.scss'

type Props = {
  criteria: SearchCriteria
  providers: IPatientConsultationStaff[]
  isSearching: boolean
  canSearch: boolean
  isDisabled?: boolean
  isDateDisabled?: (date: Dayjs) => boolean
  onChange: (patch: Partial<SearchCriteria>) => void
  onSearch: () => void
}

export const SearchBar: FC<Props> = ({
  criteria,
  providers,
  isSearching,
  canSearch,
  isDisabled,
  isDateDisabled,
  onChange,
  onSearch,
}) => (
  <div className={styles.searchBar}>
    <div className={styles.searchCell}>
      <div className={styles.widthProvider}>
        <Select
          label='Appointment with'
          value={criteria.providerId ?? undefined}
          showSearch={false}
          disabled={isDisabled}
          options={providers.map((provider) => ({ value: provider.id, label: provider.name }))}
          onChange={(value: number) => onChange({ providerId: value })}
        />
      </div>
    </div>

    <div className={styles.searchCell}>
      <Label label='Search window' id='search-window' />
      <div className={styles.inline}>
        <div className={styles.widthWindowStart}>
          <DatePicker
            id='search-window'
            aria-label='Search window start date'
            allowClear={false}
            disabled={isDisabled}
            value={dayjs(criteria.windowStart)}
            format='MMM D'
            showNow={false}
            disabledDate={isDateDisabled}
            onChange={(value) => value && onChange({ windowStart: value.format('YYYY-MM-DD') })}
          />
        </div>
        <Segmented
          className={styles.windowSegmented}
          aria-label='Search window length in days'
          value={criteria.windowDays}
          disabled={isDisabled}
          options={WINDOW_OPTIONS.map((days) => ({ label: `${days}d`, value: days }))}
          onChange={(value) => onChange({ windowDays: value as SearchCriteria['windowDays'] })}
        />
      </div>
    </div>

    <div className={styles.fieldGrow} />

    <div className={styles.searchActions}>
      <Button
        onClick={onSearch}
        loading={isSearching}
        severity={BUTTON_SEVERITY.SUCCESS_FILLED}
        disabled={!canSearch || isDisabled}>
        Find available
      </Button>
    </div>
  </div>
)
